// Harvard University (Harvard College): public college page (#2596), official facts only.
//
// Every figure below was read on 2026-09-30 from the source it names:
//  - Harvard's Common Data Set 2025-26 (CDS_2025-2026.pdf from
//    oira.harvard.edu): C1 applicants/admits/enrollees for Fall 2025; C8 test
//    policy for Fall 2027; C21/C22 early plans. C1's residency table gives
//    totals only, so no residency rows. Section G (costs) is left blank in
//    this file, so costs come from the financial aid office instead.
//  - college.harvard.edu (first-year applicants, application requirements,
//    and the 2026-2027 cost of attendance on the How Aid Works page).
//  - College Scorecard API, school id 166027 (net price by family income).
// Nothing here is profile-derived, a fit score, anyone's odds, or a
// self-reported record. Re-check a figure against its source before editing
// it, and move `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'Harvard Common Data Set 2025-2026',
        url: 'https://oira.harvard.edu/files/2026/07/CDS_2025-2026.pdf',
        read: '2026-09-30',
    },
    firstYear: {
        label: 'Harvard College Admissions, First-Year Applicants',
        url: 'https://college.harvard.edu/admissions/apply/first-year-applicants',
        read: '2026-09-30',
    },
    tests: {
        label: 'Harvard College Admissions, Application Requirements',
        url: 'https://college.harvard.edu/admissions/apply/application-requirements',
        read: '2026-09-30',
    },
    cost: {
        label: 'Harvard College Financial Aid, How Aid Works',
        url: 'https://college.harvard.edu/financial-aid/how-aid-works',
        read: '2026-09-30',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?166027-Harvard-University',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'harvard-university',
    kbId: 'harvard_university',
    name: 'Harvard University',
    shortName: 'Harvard',
    location: 'Cambridge, MA',
    control: 'Private',
    lastChecked: '2026-09-30',
    sources: SOURCES,
    // From the KB's sourced branding (Harvard design system); decoration only.
    branding: {
        primary: '#A51C30',
        secondary: '#1E1E1E',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/harvard_university_logo.png',
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 47893,
        admitted: 2003,
        enrolled: 1675,
        source: 'cds',
        where: 'section C1',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'Harvard\'s Common Data Set reports one first-year acceptance rate for Harvard College, with no rate by concentration.',
        source: 'cds',
    },
    earlyRounds: {
        title: 'Restrictive early action',
        note: 'Harvard has no early decision plan, and the Common Data Set does not report how many applied or were admitted in early action, so it gives no early acceptance rate.',
        source: 'cds',
        where: 'sections C21 and C22',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Regular Decision', value: 'January 1' },
        rows: [
            { plan: 'Restrictive Early Action', deadline: 'November 1', decision: 'Mid-December', binding: false },
            { plan: 'Regular Decision', deadline: 'January 1', decision: 'End of March', binding: false },
        ],
        note: 'Your parts of the application are due at 11:59 pm in your local time. Your counselor may send supporting materials up to a week later. In either round, you have until May 1 to decide.',
        source: 'firstYear',
        binding: 'Restrictive Early Action is not binding: if admitted, you do not have to attend. While you apply this way, you may not apply to any other private college under Early Decision, Early Action or Restrictive Early Action, or to a binding early program at a public university. You may apply early to a public university, a military academy or a university outside the United States under a nonbinding program, and you may apply elsewhere in Regular Decision or Early Decision II.',
        bindingSource: 'firstYear',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-required',
        blind: false,
        line: 'Harvard requires the SAT or ACT. Only in exceptional cases, when those tests are not accessible, can AP results, IB scores, GCSE or A-Level results, or national leaving exam results meet the requirement instead. You may self-report scores; if you enroll, you send official scores.',
        source: 'tests',
        cdsLine: 'The Common Data Set marks the SAT or ACT as required to be considered for fall 2027 admission.',
        cdsWhere: 'section C8',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-09-30',
        changes: [],
        source: 'firstYear',
    },

    cost: {
        year: '2026-2027',
        rows: [
            { item: 'Tuition', amount: 62226 },
            { item: 'Fees', amount: 6216 },
            { item: 'Housing', amount: 14250 },
            { item: 'Food', amount: 8942 },
            { item: 'Personal expenses (estimated)', amount: 2500 },
            { item: 'Books (estimated)', amount: 1000 },
            { item: 'Transportation', amount: null },
        ],
        total: 95134,
        note: 'Harvard estimates transportation at $0 to $5,000 depending on where you live, so it gives the full cost as $95,134 to $100,134. Health insurance ($4,954 for 2026-27) is also required unless your family plan covers you.',
        source: 'cost',
        netPrice: {
            average: 19148,
            byIncome: [
                { income: '$0 to $30,000', amount: 2542 },
                { income: '$30,001 to $48,000', amount: 2070 },
                { income: '$48,001 to $75,000', amount: 4126 },
                { income: '$75,001 to $110,000', amount: 9995 },
                { income: 'Over $110,000', amount: 51128 },
            ],
            note: 'What first-year, full-time students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from Harvard\'s aid offer.',
            tileDetail: 'Per year, after grants, federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
